import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  FilterDropdownComponent,
  FilterDropdownOption,
} from '../../../components/filter-dropdown/filter-dropdown.component';
import { ResetFilterButtonComponent } from '../../../components/reset-filter-button/reset-filter-button.component';
import { SavedViewOption, SavedViewsMenuComponent } from '../../../shared/saved-views/saved-views-menu.component';

export interface FilterOption {
  value: string;
  label: string;
}

export type ModuleFilterKey = 'status' | 'author';

export interface FilterChange {
  key: ModuleFilterKey;
  value: string | null;
}

@Component({
  selector: 'app-module-filter-bar',
  standalone: true,
  imports: [TranslatePipe, FilterDropdownComponent, ResetFilterButtonComponent, SavedViewsMenuComponent],
  templateUrl: './module-filter-bar.component.html',
  styleUrl: './module-filter-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleFilterBarComponent {
  readonly statusOptions = input<FilterOption[]>([]);
  readonly authorOptions = input<FilterOption[]>([]);

  readonly selectedStatus = input<string[]>([]);
  readonly selectedAuthor = input<string[]>([]);

  readonly savedViews = input<SavedViewOption[]>([]);
  readonly activeSavedView = input<string | null>(null);
  readonly projectId = input.required<string>();
  readonly hasActiveFilters = input<boolean>(false);

  readonly filterChange = output<FilterChange>();
  readonly resetFilters = output<void>();
  readonly savedViewSelected = output<string>();
  readonly savedViewSaved = output<string>();
  readonly savedViewDeleted = output<string>();
  readonly savedViewDeleteFailed = output<string>();

  readonly statusValue = computed(() => this.selectedStatus()[0] ?? '');
  readonly authorValue = computed(() => this.selectedAuthor()[0] ?? '');

  // NEX-1058 Review (fboudouhi Blocker 2): ein aktiver Filterwert, der nicht in
  // der datenabgeleiteten Optionsliste steht (gespeicherte Ansicht mit
  // status=READY, keine READY-Module mehr im Projekt), waere sonst nicht in
  // einem Klick loeschbar — der Browser faellt auf den Placeholder zurueck,
  // ein Klick auf ihn feuert kein change-Event, weil selectedIndex bereits 0
  // ist. Muster gespiegelt aus requirements-filter-bar (#698).
  private toDropdownOptions(values: FilterOption[], active: string): FilterDropdownOption[] {
    if (active && !values.some((o) => o.value === active)) {
      return [...values, { value: active, label: active }];
    }
    return values;
  }

  readonly statusDropdownOptions = computed<FilterDropdownOption[]>(() =>
    this.toDropdownOptions(this.statusOptions(), this.statusValue()),
  );
  readonly authorDropdownOptions = computed<FilterDropdownOption[]>(() =>
    this.toDropdownOptions(this.authorOptions(), this.authorValue()),
  );

  onChange(key: ModuleFilterKey, raw: string): void {
    this.filterChange.emit({ key, value: raw || null });
  }

  onSavedViewSelected(name: string): void {
    this.savedViewSelected.emit(name);
  }

  onSavedViewSaved(name: string): void {
    this.savedViewSaved.emit(name);
  }

  onSavedViewDeleted(name: string): void {
    this.savedViewDeleted.emit(name);
  }

  onSavedViewDeleteFailed(name: string): void {
    this.savedViewDeleteFailed.emit(name);
  }
}
